import Auth from "@aws-amplify/auth";
import { Hub } from '@aws-amplify/core';
import { config } from "../config/config";
import constants from "../config/constants";

 
let sessionInterval: NodeJS.Timeout | null = null;

/**
 * Redirects to the Cognito login page with LASSO provider
 */
function redirectToLoginPage() {
  console.log('[Auth-LASSO] Redirecting to LASSO login page');
  
  Auth.configure((config as any).AMPLIFY_CONFIG);
  
  // Use LASSO as the identity provider
  (Auth.federatedSignIn as any)({
    provider: constants.AUTH_PROVIDER_LASSO,
    customState: window.location.origin,
  });
}

/**
 * Refresh token for LASSO authentication
 */
async function refreshToken() {
  try {
    console.log('[Auth-LASSO] Token refresh starting...');
    
    // Try to get current authenticated user first
    const user = await Auth.currentAuthenticatedUser();
    
    // Get the current session which should handle refresh automatically
    const currentSession = await Auth.currentSession();
    
    const refreshedToken = currentSession.getIdToken().getJwtToken();
    const expiration = currentSession.getIdToken().getExpiration();
    
    console.log('[Auth-LASSO] Token refresh successful, new expiration:', new Date(expiration * 1000).toISOString());
    return { refreshedToken, expiration };
  } catch (error) {
    console.error('[Auth-LASSO] Token refresh failed:', error);
    
    // Don't redirect here - just throw the error
    // The calling code should handle the redirect to avoid loops
    throw error;
  }
}

/**
 * Starts the session timer
 */
async function startSessionTimer() {
  console.log('[Auth-LASSO] startSessionTimer - Starting session timer');
  
  const fiveMinutes = 60 * 5;
  const twentyHour = 43200;
  const refreshTokenValidity = (config as any).REFRESH_TOKEN_VALIDITY * twentyHour;
  let sessionWillExpireNotice = false;

  try {
    const currentSession = await Auth.currentSession();
    console.log('[Auth-LASSO] Current session retrieved');
    
    const accessToken = currentSession.getAccessToken();
    const expiresTimestamp = accessToken.getIssuedAt() + refreshTokenValidity;
    console.log('[Auth-LASSO] Session expires at:', new Date(expiresTimestamp * 1000).toISOString());

    sessionInterval = setInterval(() => {
      const currentTimestamp = new Date().getTime() / 1000;
      const timeRemaining = expiresTimestamp - currentTimestamp;

      if (!sessionWillExpireNotice && timeRemaining < fiveMinutes) {
        sessionWillExpireNotice = true;
        console.log('[Auth-LASSO] Session will expire in less than 5 minutes');
        Hub.dispatch(constants.SESSION_WILL_EXPIRE, {
          event: constants.SESSION_WILL_EXPIRE,
        });
      } else if (timeRemaining <= 0) {
        console.log('[Auth-LASSO] Session expired');
        Hub.dispatch(constants.SESSION_EXPIRED, {
          event: constants.SESSION_EXPIRED,
        });
      }
    }, constants.SESSION_TIMER_INTERVAL);
    
    console.log('[Auth-LASSO] Session timer started');
  } catch (e) {
    console.error('[Auth-LASSO] startSessionTimer - Error:', e);
    Hub.dispatch(constants.SESSION_EXPIRED, {
      event: constants.SESSION_EXPIRED,
    });
  }
}

/**
 * Retrieves the JWT token for the currently authenticated user
 */
async function getAccessToken(): Promise<string> {
  let accessToken = "";

  try {
    const user = await Auth.currentAuthenticatedUser();
    accessToken = user.signInUserSession.idToken.jwtToken;
  } catch (ex) {
    console.error("[Auth-LASSO] getAccessToken - Failed to get access token:", ex);
  }

  return accessToken;
}

/**
 * Checks if user has access tokens
 */
async function hasAccessTokens(): Promise<boolean> {
  try {
    await Auth.currentAuthenticatedUser();
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Initializes the session
 */
async function initializeSession(
  onSessionExpired: () => void = () => {},
  onSessionWillExpire: (willExpire: boolean) => void = () => {},
  onUserProfileChange: (user: any) => void = () => {}
) {
  try {
    const user = await Auth.currentAuthenticatedUser();
    onUserProfileChange(user);

    Hub.listen(constants.SESSION_EXPIRED, () => {
      if (sessionInterval) {
        clearInterval(sessionInterval);
      }
      signOut();
      onSessionExpired();
    });

    Hub.listen(constants.SESSION_WILL_EXPIRE, () => {
      onSessionWillExpire(true);
    });

    setTimeout(startSessionTimer, constants.SESSION_TIMER_INTERVAL);
  } catch (error) {
    console.error('[Auth-LASSO] initializeSession - Error:', error);
    throw error;
  }
}

/**
 * Signs the user out
 */
async function signOut() {
  console.log('[Auth-LASSO] signOut - Signing user out');
  
  try {
    await Auth.signOut();
    console.log('[Auth-LASSO] User signed out successfully');
  } catch (error) {
    console.error('[Auth-LASSO] signOut - Error:', error);
  }
}

/**
 * Gets user information including username from LASSO
 */
async function getUserInfo(): Promise<any> {
  
  console.log('[Auth-LASSO] ========== GET USER INFO ==========');
  console.log('[Auth-LASSO] Attempting to get current authenticated user...');
  
  let userInfo: any = null;
  let userName = "";
  let email = "";
  let familyName ='';
  let givenName = "";
  try {
    await Auth.currentAuthenticatedUser()
      .then(user => {
        console.log('[Auth-LASSO] User found:', user ? 'YES' : 'NO');
        const session = user.getSignInUserSession();
        const idToken = session.getIdToken();
        const accessToken = session.getAccessToken();
        const refreshToken = session.getRefreshToken();
        
        // Decode ID token payload
        const payload = idToken.decodePayload();
        
        // Combine payload with token information
        userInfo = {
          ...payload,
          idToken: idToken.getJwtToken(),
          accessToken: accessToken.getJwtToken(),
          refreshToken: refreshToken.getToken(),
          tokenExpiration: idToken.getExpiration(),
          authTime: payload.auth_time,
        };
        console.log('userInfo', userInfo)
        // Extract username based on provider
        if (userInfo && userInfo.identities && userInfo.identities[0]) {
          const providerName = userInfo.identities[0].providerName;
          
          if (providerName === constants.AUTH_PROVIDER_LASSO) {
            // LASSO: use custom:username attribute
            userName = userInfo['custom:username'];
            email = userInfo['email'];
            familyName = userInfo['family_name'];
            givenName = userInfo['given_name'];
             
          } else {
            // Fallback for other providers
            userName = userInfo.identities[0].userId;
          }
        }
      })
      .catch(err => {
        console.error('[Auth-LASSO] Failed to get authenticated user:', err.message);
        console.error('[Auth-LASSO] Error details:', err);
      });
  } catch (err) {
    console.error('[Auth-LASSO] getUserInfo outer catch error:', err);
  }

  console.log('[Auth-LASSO] Final userInfo:', userInfo ? 'HAS DATA' : 'NULL');
  console.log('[Auth-LASSO] ========== END GET USER INFO ==========');
  
  if (userInfo) {
    return new Promise(resolve => resolve(userInfo));
  }
  
  return null;
}

export {
  hasAccessTokens,
  initializeSession,
  getAccessToken,
  redirectToLoginPage,
  signOut,
  getUserInfo,
  refreshToken,
};
